import { BRAND_COLORS, type ProgressResponse } from '@lk/shared';

export interface ProgressChartProps {
  daily: ProgressResponse['daily'];
  height?: number;
}

/**
 * Графік прогресу в часі: стовпчики — кількість відповідей за день,
 * лінія — влучність. Малюємо руками на SVG: одна залежність менше,
 * і виглядає рівно так, як треба нам.
 */
export function ProgressChart({ daily, height = 160 }: ProgressChartProps): React.JSX.Element {
  if (daily.length === 0) {
    return (
      <p className="px-1 py-6 text-center text-sm font-semibold text-white/45">
        Даних поки немає — зʼявляться після перших сесій.
      </p>
    );
  }

  const width = Math.max(320, daily.length * 26);
  const padding = { top: 12, right: 8, bottom: 22, left: 8 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const maxAttempts = Math.max(1, ...daily.map((d) => d.attempts));
  const step = innerW / daily.length;
  const barWidth = Math.max(4, step * 0.55);

  const accuracyPoints = daily
    .map((day, index) => {
      const accuracy = day.attempts > 0 ? day.correct / day.attempts : 0;
      const x = padding.left + index * step + step / 2;
      const y = padding.top + innerH * (1 - accuracy);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const totalAttempts = daily.reduce((sum, d) => sum + d.attempts, 0);
  const totalCorrect = daily.reduce((sum, d) => sum + d.correct, 0);

  return (
    <div className="lk-scroll-x">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Графік за ${daily.length} днів: усього ${totalAttempts} відповідей, з них ${totalCorrect} правильних.`}
      >
        {[0.25, 0.5, 0.75].map((line) => (
          <line
            key={line}
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + innerH * line}
            y2={padding.top + innerH * line}
            stroke="rgba(255,255,255,.08)"
            strokeWidth={1}
          />
        ))}

        {daily.map((day, index) => {
          const barHeight = (day.attempts / maxAttempts) * innerH;
          const x = padding.left + index * step + (step - barWidth) / 2;
          const y = padding.top + innerH - barHeight;
          return (
            <rect
              key={day.date}
              x={x}
              y={y}
              width={barWidth}
              height={Math.max(barHeight, day.attempts > 0 ? 2 : 0)}
              rx={3}
              fill={BRAND_COLORS.cyan}
              opacity={0.65}
            >
              <title>{`${day.date}: ${day.attempts} відповідей, ${day.coins} балів`}</title>
            </rect>
          );
        })}

        <polyline
          points={accuracyPoints}
          fill="none"
          stroke={BRAND_COLORS.gold}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {daily.map((day, index) =>
          index % Math.ceil(daily.length / 7) === 0 ? (
            <text
              key={`label-${day.date}`}
              x={padding.left + index * step + step / 2}
              y={height - 6}
              textAnchor="middle"
              fontSize={9}
              fontWeight={800}
              fill="rgba(255,255,255,.45)"
            >
              {day.date.slice(5)}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
}
